import axios, { AxiosError, AxiosRequestConfig } from 'axios';

import { refreshAccessToken } from './token-refresh';
import { isSonioCmsApiUrl } from 'src/services/api.config';

/**
 * Decode JWT token to get expiration time
 */
const decodeToken = (token: string): { exp?: number; iat?: number } | null => {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join(''),
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    return null;
  }
};

/**
 * Check if a token is expired or will expire soon (within 60 seconds)
 */
const isTokenExpired = (token: string): boolean => {
  const decoded = decodeToken(token);
  if (!decoded || !decoded.exp) {
    return true; // If we can't decode, assume expired
  }
  // Check if token expires within the next 60 seconds
  const expirationTime = decoded.exp * 1000; // Convert to milliseconds
  const currentTime = Date.now();
  const bufferTime = 60 * 1000; // 60 seconds buffer
  return currentTime >= expirationTime - bufferTime;
};

// This file globally configures axios to include the access token, if present,
// on Sonio API calls. Imported once in src/index.tsx.

// Request interceptor to attach tokens
axios.interceptors.request.use(async (config: AxiosRequestConfig) => {
  const url = config.url || '';
  const isSonioApi =
    url.startsWith('https://staging.id.sonio-group.com/') ||
    url.startsWith('https://dev.id.sonio-group.com/') ||
    isSonioCmsApiUrl(url);

  if (isSonioApi) {
    // Ensure headers object exists
    if (!config.headers) {
      config.headers = {};
    }

    // Check if Authorization header is already set in the config
    // When headers are passed in axios.get(url, { headers: {...} }),
    // they're set directly on config.headers
    const headers = config.headers as Record<string, any>;

    // Check all possible locations for the Authorization header
    let authHeaderValue: string | undefined;

    // First check direct header properties (case-insensitive)
    for (const key in headers) {
      if (key.toLowerCase() === 'authorization') {
        authHeaderValue = headers[key];
        break;
      }
    }

    // If not found, check common headers (axios defaults)
    if (!authHeaderValue && (headers as any).common) {
      const common = (headers as any).common;
      for (const key in common) {
        if (key.toLowerCase() === 'authorization') {
          authHeaderValue = common[key];
          break;
        }
      }
    }

    if (authHeaderValue) {
      // Authorization header is already set explicitly - check if it's expired
      const tokenMatch = String(authHeaderValue).match(/^Bearer\s+(.+)$/);
      if (tokenMatch && tokenMatch[1]) {
        const token = tokenMatch[1];

        // Check if the explicitly provided token is expired
        if (isTokenExpired(token)) {
          // Token is expired, try to refresh it
          const refreshedToken = await refreshAccessToken();
          if (refreshedToken) {
            // Update the header with the refreshed token
            headers['Authorization'] = `Bearer ${refreshedToken}`;
            headers['authorization'] = `Bearer ${refreshedToken}`;
            // Update localStorage
            localStorage.setItem('idToken', refreshedToken);
            return config;
          }
          // If refresh failed, continue with the expired token
          // The 401 interceptor will handle it
        } else {
          // Token is valid, update localStorage with the fresh token
          const currentStoredToken = localStorage.getItem('idToken');
          if (currentStoredToken !== token) {
            localStorage.setItem('idToken', token);
          }
        }
      }
      // Return early to avoid overriding the explicit header
      return config;
    }

    // No Authorization header set - use token from localStorage
    // But first check if it's expired and refresh if needed
    let idToken = localStorage.getItem('idToken');
    if (idToken) {
      // Check if token is expired or about to expire
      if (isTokenExpired(idToken)) {
        // Token is expired, try to refresh it proactively
        const refreshedToken = await refreshAccessToken();
        if (refreshedToken) {
          // Use the refreshed token
          idToken = refreshedToken;
          localStorage.setItem('idToken', refreshedToken);
        }
        // If refresh failed, continue with the existing token
        // The 401 interceptor will handle it
      }
      config.headers['Authorization'] = `Bearer ${idToken}`;
    }
  }
  return config;
});

// Response interceptor to handle 401 errors and refresh tokens
axios.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & {
      _retry?: boolean;
    };

    // Check if it's a 401 error and we haven't already retried
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const url = originalRequest.url || '';
      const isSonioApi =
        url.startsWith('https://staging.id.sonio-group.com/') ||
        url.startsWith('https://dev.id.sonio-group.com/') ||
        isSonioCmsApiUrl(url);

      // Only attempt token refresh for Sonio API calls
      if (isSonioApi) {
        try {
          // Always use refresh token mechanism when we get a 401
          // This ensures we get a fresh token even if the cache has a stale one
          // eslint-disable-next-line no-console
          console.log(
            '[Axios Interceptor] 401 detected, refreshing token for:',
            url,
          );
          const newIdToken = await refreshAccessToken();

          if (!newIdToken) {
            // eslint-disable-next-line no-console
            console.error('[Axios Interceptor] Failed to refresh token');
            return Promise.reject(error);
          }

          // eslint-disable-next-line no-console
          console.log('[Axios Interceptor] Token refreshed, retrying request');

          if (newIdToken) {
            // Ensure headers object exists
            if (!originalRequest.headers) {
              originalRequest.headers = {};
            }

            // Clear any existing Authorization header (case-insensitive)
            // Axios normalizes headers, but we'll clear both to be safe
            if (originalRequest.headers['Authorization']) {
              delete originalRequest.headers['Authorization'];
            }
            if (originalRequest.headers['authorization']) {
              delete originalRequest.headers['authorization'];
            }

            // Set the new idToken in the header - use the exact case axios expects
            originalRequest.headers['Authorization'] = `Bearer ${newIdToken}`;

            // Ensure the config knows we've modified it
            originalRequest._retry = true;

            // Create a new config object to ensure headers are properly set
            const retryConfig: AxiosRequestConfig = {
              ...originalRequest,
              headers: {
                ...originalRequest.headers,
                Authorization: `Bearer ${newIdToken}`,
              },
            };

            // Retry the original request with the new idToken
            // The request interceptor will respect the existing Authorization header
            return axios(retryConfig);
          }
        } catch (refreshError) {
          // If refresh fails, reject the original error
          return Promise.reject(error);
        }
      }
    }

    // For non-401 errors or if refresh failed, reject with the original error
    return Promise.reject(error);
  },
);
